import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import type { AppUser } from "./types";

type AuthStatus =
  | "loading" // ainda verificando sessao existente
  | "signed_out" // sem sessao
  | "checking_allowlist" // logado, checando app_users
  | "allowed" // logado + na allowlist ativa
  | "not_allowed"; // logado, mas sem linha ativa em app_users

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  appUser: AppUser | null;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (
    email: string,
    password: string,
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (cancelled) return;

      if (!currentUser) {
        setUser(null);
        setAppUser(null);
        setStatus("signed_out");
        return;
      }

      setUser(currentUser);
      setStatus("checking_allowlist");

      // Usuario autenticado sem linha ATIVA em app_users nao enxerga nada:
      // as Security Rules ja garantem isso no Firestore (ver
      // firestore.rules), mas checar aqui evita renderizar telas que so vao
      // falhar silenciosamente com listas vazias.
      //
      // A leitura abaixo e negada pelas rules ATE a linha existir (nao e
      // so "documento vazio" — e permission-denied de verdade), entao o
      // try/catch e essencial: sem ele, a tela travaria pra sempre em
      // "Verificando sessao" pra qualquer usuario ainda fora da allowlist,
      // que e exatamente quem mais precisa ver a tela de acesso negado.
      let data: AppUser | null = null;
      try {
        const snap = await getDoc(doc(db, "app_users", currentUser.uid));
        data = snap.exists() ? (snap.data() as AppUser) : null;
      } catch {
        data = null;
      }
      if (cancelled) return;

      if (!data || !data.ativo) {
        setAppUser(null);
        setStatus("not_allowed");
        return;
      }

      setAppUser({ ...data, id: currentUser.uid });
      setStatus("allowed");
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      appUser,
      async signInWithGoogle() {
        await signInWithPopup(auth, new GoogleAuthProvider());
      },
      async signInWithPassword(email, password) {
        try {
          await signInWithEmailAndPassword(auth, email, password);
          return { error: null };
        } catch (e) {
          return { error: (e as Error).message };
        }
      },
      async signOut() {
        await firebaseSignOut(auth);
      },
    }),
    [status, user, appUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth precisa ser usado dentro de <AuthProvider>.");
  }
  return ctx;
}
